import React, { useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Stack,
  Paper,
} from "@mui/material";
import { partyCreate, partyList } from "../../../Services/Services";
import { brandColor as brand } from "../../../theme";
import { toast } from "react-toastify";
const brandBg = "rgba(79, 70, 229, 0.06)";

const ConsigneeForm = ({ setParties, setOpenAddModal }) => {
  const [partyDetails, setPartyDetails] = useState({
    name: "",
    contact_no: "",
    contact_no1: "",
    address: "",
    gst_no: "",
    pan_no: "",
  });

  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [duplicateParty, setDuplicateParty] = useState(null);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    const updatedErrors = { ...errors };

    if (name.includes("contact_no")) {
      newValue = value.replace(/\D/g, "");
    }
    if (name === "gst_no") newValue = value.toUpperCase().slice(0, 15);
    if (name === "pan_no") newValue = value.toUpperCase().slice(0, 10);

    setPartyDetails({ ...partyDetails, [name]: newValue });
    setErrors(updatedErrors);
  };

  const validateInputs = () => {
    const { contact_no, gst_no, pan_no, name, address } = partyDetails;
    const validationErrors = {};

    if (!name.trim()) validationErrors.name = "Consignee name is required";
    if (!address.trim()) validationErrors.address = "Address is required";
    if (!/^\d{10}$/.test(contact_no))
      validationErrors.contact_no = "Contact number must be exactly 10 digits.";
    if (
      gst_no &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst_no)
    )
      validationErrors.gst_no = "Invalid GST No format.";
    if (pan_no && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan_no))
      validationErrors.pan_no = "Invalid PAN No format.";

    return validationErrors;
  };

  const checkDuplicate = async () => {
    try {
      const allParties = await partyList({ party_type: "consignee" }); // ← filter
      const duplicate = allParties.find(
        (p) =>
          p.name?.toLowerCase().trim() ===
          partyDetails.name?.toLowerCase().trim(),
      );
      if (duplicate) {
        setDuplicateParty(duplicate);
        setOpenDuplicateDialog(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error fetching parties:", error);
      return false;
    }
  };

  const saveParty = async (force = false) => {
    try {
      const res = await partyCreate({
        ...partyDetails,
        force,
        party_type: "consignee",
      });

      if (res?.duplicate && !force) {
        setDuplicateParty(res);
        setOpenDuplicateDialog(true);
        return;
      }

      if (res?.parties) {
        setParties(res.parties);
      } else {
        const latestParties = await partyList({ party_type: "consignee" });
        setParties(latestParties);
      }

      setOpenAddModal(false);
      setPartyDetails({
        name: "",
        contact_no: "",
        contact_no1: "",
        address: "",
        gst_no: "",
        pan_no: "",
      });
      toast.success("Consignee added successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add consignee.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateInputs();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const isDuplicate = await checkDuplicate();
    if (!isDuplicate) {
      await saveParty();
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      <form onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2.5} sx={{ p: 2.5 }}>
          {/* Consignee Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Consignee Name *"
              name="name"
              value={partyDetails.name}
              onChange={handleChange}
              required
              error={!!errors.name}
              helperText={errors.name}
              size="small"
            />
          </Grid>

          {/* Contact Number */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Contact No *"
              name="contact_no"
              value={partyDetails.contact_no}
              onChange={handleChange}
              required
              error={!!errors.contact_no}
              helperText={errors.contact_no}
              inputProps={{ maxLength: 10 }}
              size="small"
            />
          </Grid>

          {/* Alternate Mobile 1 */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Alternate Mobile No"
              name="contact_no1"
              value={partyDetails.contact_no1}
              onChange={handleChange}
              inputProps={{ maxLength: 10 }}
              size="small"
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address *"
              name="address"
              value={partyDetails.address}
              onChange={handleChange}
              multiline
              rows={2}
              required
              error={!!errors.address}
              helperText={errors.address}
              size="small"
            />
          </Grid>

          {/* GST No */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="GST No"
              name="gst_no"
              value={partyDetails.gst_no}
              onChange={handleChange}
              inputProps={{ maxLength: 15 }}
              error={!!errors.gst_no}
              helperText={errors.gst_no}
              size="small"
            />
          </Grid>

          {/* PAN No */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="PAN No"
              name="pan_no"
              value={partyDetails.pan_no}
              onChange={handleChange}
              inputProps={{ maxLength: 10 }}
              error={!!errors.pan_no}
              helperText={errors.pan_no}
              size="small"
            />
          </Grid>

          {/* Buttons */}
          <Grid item xs={12}>
            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="flex-end"
              sx={{ mt: 1 }}
            >
              <Button
                variant="outlined"
                onClick={() => setOpenAddModal(false)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "0.75rem",
                  px: 2,
                  py: 0.75,
                  borderColor: "#d1d5db",
                  color: "#6b7280",
                  "&:hover": { borderColor: brand, color: brand },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "0.75rem",
                  px: 2.5,
                  py: 0.75,
                  bgcolor: brand,
                  "&:hover": { bgcolor: "#4338ca" },
                }}
              >
                Add Consignee
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          top: "80px !important",
        }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Duplicate Dialog */}
      <Dialog
        open={openDuplicateDialog}
        onClose={() => setOpenDuplicateDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <Box sx={{ bgcolor: brand, px: 2.5, py: 1.5 }}>
          <Typography
            sx={{ fontWeight: 700, color: "#fff", fontSize: "0.9rem" }}
          >
            Duplicate Consignee Found
          </Typography>
        </Box>
        <DialogContent sx={{ p: 2.5 }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#333", mb: 1 }}>
            A consignee with the name <strong>"{partyDetails.name}"</strong>{" "}
            already exists.
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "#666" }}>
            Do you want to add it again anyway?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setOpenDuplicateDialog(false)}
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setOpenDuplicateDialog(false);
              saveParty(true); // ✅ force = true
            }}
            variant="contained"
            size="small"
            sx={{ borderRadius: 2, textTransform: "none", bgcolor: brand }}
          >
            Continue Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsigneeForm;
